import { normalizeUrlPath } from "./policies";

const own = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);
const object = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const blank = (value) => value === undefined || value === null || (typeof value === "string" && value.trim() === "");

const parseData = (value) => {
  if (object(value)) return value;
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return object(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const required = (errors, key, value, label, method) => {
  if (method && (method === "post" || value !== undefined) && blank(value)) {
    errors[key] = `${label} est obligatoire.`;
  }
};

const string = (errors, key, value, label, max, method) => {
  required(errors, key, value, label, method);
  if (blank(value)) return;
  if (typeof value !== "string") {
    errors[key] = `${label} doit être une chaîne de caractères.`;
  } else if (max && value.length > max) {
    errors[key] = `${label} ne peut pas dépasser ${max} caractères.`;
  }
};

const identifier = (errors, key, value, label, method) => {
  required(errors, key, value, label, method);
  if (!blank(value) && !["string", "number"].includes(typeof value)) {
    errors[key] = `${label} doit être un identifiant valide.`;
  }
};

const numeric = (errors, key, value, label, { integer = false, min } = {}) => {
  if (blank(value)) return;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || (integer && !Number.isInteger(parsed)) || (min !== undefined && parsed < min)) {
    errors[key] = `${label} doit être un nombre${integer ? " entier" : ""} valide.`;
  }
};

const binaryStatus = (errors, key, value, label = "Le statut") => {
  if (!blank(value) && ![0, 1].includes(Number(value))) {
    errors[key] = `${label} doit valoir 0 ou 1.`;
  }
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;
const TIME = /^(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/;

const validDate = (value) => {
  if (blank(value)) return true;
  if (typeof value !== "string" || !ISO_DATE.test(value)) return false;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return false;
  return !/^\d{4}-\d{2}-\d{2}$/.test(value) || parsed.toISOString().slice(0, 10) === value;
};

const date = (errors, key, value, label, method) => {
  required(errors, key, value, label, method);
  if (!validDate(value)) errors[key] = `${label} doit être une date ISO valide.`;
};

const time = (errors, key, value, label) => {
  if (!blank(value) && (typeof value !== "string" || !TIME.test(value))) {
    errors[key] = `${label} doit respecter le format HH:mm ou HH:mm:ss.`;
  }
};

const nested = (errors, data, key, label, method) => {
  if (object(data[key])) return data[key];
  if (method === "post" || own(data, key)) errors[key] = `${label} doit être un objet JSON.`;
  return {};
};

const validateUser = (errors, user, method) => {
  string(errors, "last_name", user.last_name, "Le nom", 50, method);
  [
    ["first_name", 50, "Le prénom"], ["family_name", 50, "Le post-nom"],
    ["marital_status", 20, "L’état civil"], ["genre", 10, "Le genre"],
    ["identity_number", 50, "Le numéro d’identité"], ["email", 80, "L’adresse e-mail"],
    ["tel", 50, "Le téléphone"], ["adress", 100, "L’adresse"],
    ["profession", 100, "La profession"], ["nationality", 100, "La nationalité"],
    ["avatar", 250, "L’avatar"], ["expo_token", 80, "Le jeton Expo"],
    ["asset_id", 150, "L’identifiant du fichier"],
  ].forEach(([key, max, label]) => string(errors, key, user[key], label, max));
  date(errors, "datebirth", user.datebirth, "La date de naissance");
  numeric(errors, "scolary_year", user.scolary_year, "L’année scolaire", { integer: true });
  binaryStatus(errors, "share", user.share, "Le partage");
};

const validators = {
  sections(data, errors, method) {
    string(errors, "short_entitled_section", data.short_entitled_section, "L’abréviation", 20, method);
    string(errors, "entitled_section", data.entitled_section, "L’intitulé", 50, method);
    string(errors, "operation", data.operation, "L’opération", 20, method);
    identifier(errors, "yearId", data.yearId, "L’année scolaire");
  },

  sectionclasses(data, errors, method) {
    const classeId = data.classeId ?? data.classId;
    identifier(errors, "classeId", classeId, "La classe", method);
    identifier(errors, "sectionId", data.sectionId, "La section", method);
    identifier(errors, "reportcardcategoryId", data.reportcardcategoryId, "Le type de bulletin", method);
    identifier(errors, "yearId", data.yearId, "L’année scolaire", method);
    string(errors, "operation", data.operation, "L’opération", 20, method);
    string(errors, "title", data.title, "Le titre du groupe", 5);
  },

  students(data, errors, method) {
    const user = nested(errors, data, "user", "Les données utilisateur", method);
    const student = nested(errors, data, "student", "Les données élève", method);
    validateUser(errors, user, method);
    identifier(errors, "sectionclasseId", student.sectionclasseId, "La classe", method);
    identifier(errors, "yearId", student.yearId, "L’année scolaire", method);
    string(errors, "operation", student.operation, "L’opération", 20, method);
    [
      ["number_perm", 50, "Le numéro permanent"], ["numberId", 20, "Le numéro ID"],
      ["matricule", 20, "Le matricule"], ["staff_student", 10, "Le statut interne"],
      ["code", 20, "Le code"],
    ].forEach(([key, max, label]) => string(errors, key, student[key], label, max));
    ["studentmax", "studentsuccess", "purcent"].forEach((key) => numeric(errors, key, student[key], key));
    numeric(errors, "number_house", student.number_house, "Le numéro de maison", { integer: true, min: 0 });
    binaryStatus(errors, "status", student.status);
  },

  staffs(data, errors, method) {
    const user = nested(errors, data, "user", "Les données utilisateur", method);
    const staff = nested(errors, data, "staff", "Les données du personnel", method);
    validateUser(errors, user, method);
    string(errors, "operation", staff.operation, "L’opération", 20, method);
    string(errors, "password", staff.password, "Le mot de passe initial", null, method);
    [
      ["matricule", 50, "Le matricule"], ["seniority", 50, "L’ancienneté"],
      ["diploma", 50, "Le diplôme"], ["file_diploma", 100, "Le fichier du diplôme"],
    ].forEach(([key, max, label]) => string(errors, key, staff[key], label, max));
    date(errors, "date_function", staff.date_function, "La date de fonction");
    date(errors, "commitment_date", staff.commitment_date, "La date d’engagement");
    numeric(errors, "salary", staff.salary, "Le salaire", { min: 0 });
  },

  leaves(data, errors, method) {
    string(errors, "reason", data.reason, "Le motif", null, method);
    date(errors, "date_start", data.date_start, "La date de début", method);
    date(errors, "date_end", data.date_end, "La date de fin", method);
    identifier(errors, "staffId", data.staffId, "Le membre du personnel", method);
    identifier(errors, "yearId", data.yearId, "L’année scolaire");
    binaryStatus(errors, "status", data.status);
    if (validDate(data.date_start) && validDate(data.date_end) && !blank(data.date_start) && !blank(data.date_end) && new Date(data.date_end) < new Date(data.date_start)) {
      errors.date_end = "La date de fin doit être postérieure ou égale à la date de début.";
    }
  },

  presences(data, errors, method) {
    date(errors, "date", data.date, "La date", method);
    time(errors, "time_start", data.time_start, "L’heure de début");
    time(errors, "time_end", data.time_end, "L’heure de fin");
    identifier(errors, "staffId", data.staffId, "Le membre du personnel");
    identifier(errors, "studentId", data.studentId, "L’élève");
  },

  daystaffs(data, errors, method) {
    string(errors, "actif", data.actif, "Le statut", 20, method);
    identifier(errors, "dayId", data.dayId, "Le jour", method);
    // yearId is one of the foreign keys commonly injected from the session.
    identifier(errors, "yearId", data.yearId, "L’année scolaire");
    identifier(errors, "staffId", data.staffId, "Le membre du personnel");
  },

  parentfamilies(data, errors, method) {
    validateUser(errors, data, method);
    string(errors, "operation", data.operation, "L’opération", 20, method);
    identifier(errors, "profileId", data.profileId, "Le profil");
    identifier(errors, "userId", data.userId, "L’utilisateur");
    identifier(errors, "yearId", data.yearId, "L’année scolaire");
  },

  suppliers(data, errors, method) {
    validateUser(errors, data, method);
    string(errors, "contact", data.contact, "Le contact", 50);
    string(errors, "name", data.name, "Le nom du fournisseur", 100);
    string(errors, "operation", data.operation, "L’opération", 20, method);
    identifier(errors, "profileId", data.profileId, "Le profil");
    identifier(errors, "userId", data.userId, "L’utilisateur");
    identifier(errors, "yearId", data.yearId, "L’année scolaire");
  },

  territories(data, errors, method) {
    string(errors, "name", data.name, "Le nom du territoire", 50, method);
  },

  communes(data, errors, method) {
    string(errors, "name", data.name, "Le nom de la commune", 50, method);
    identifier(errors, "territoryId", data.territoryId, "Le territoire");
  },

  groupments(data, errors, method) {
    string(errors, "name", data.name, "Le nom du groupement", 50, method);
    identifier(errors, "territoryId", data.territoryId, "Le territoire");
  },

  quartiers(data, errors, method) {
    string(errors, "name", data.name, "Le nom du quartier", 50, method);
    identifier(errors, "communeId", data.communeId, "La commune");
    identifier(errors, "groupmentId", data.groupmentId, "Le groupement");
  },
};

export const validateApiMutation = (config) => {
  const method = (config?.method || "get").toLowerCase();
  if (!["post", "put"].includes(method)) return {};

  const parts = normalizeUrlPath(config?.url).split("/").filter(Boolean);
  const validate = validators[parts[0]];
  const crudRoute = (method === "post" && parts.length === 1) || (method === "put" && parts.length === 2);
  if (!validate || !crudRoute) return {};

  const data = parseData(config.data);
  if (!data) return { data: "Le corps de la requête doit être un objet JSON valide." };
  if (method === "put" && Object.keys(data).length === 0) {
    return { data: "La mise à jour doit contenir au moins un champ." };
  }

  const errors = {};
  validate(data, errors, method);
  return errors;
};

export const createApiValidationError = (config, errors) => {
  const message = Object.values(errors)[0] || "Les données sont invalides.";
  const error = new Error(message);
  error.code = "API_VALIDATION_ERROR";
  error.config = config;
  error.response = {
    status: 400,
    statusText: "Validation Error",
    headers: {},
    config,
    data: { msg: message, errors },
  };
  return error;
};
